import type { KeyPoseGhostOptionsState, KeyPoseId } from './types';

export type KeyPoseOnionSkinDirection = 'previous' | 'future';

export interface KeyPoseOnionSkinFrameEntry {
  keyPoseId: KeyPoseId;
  playbackFrame: number;
}

export interface KeyPoseOnionSkinDescriptor extends KeyPoseOnionSkinFrameEntry {
  direction: KeyPoseOnionSkinDirection;
  opacity: number;
  rank: number;
}

export const DEFAULT_KEY_POSE_ONION_SKIN_OPTIONS = {
  enabled: false,
  ghostsPerDirection: 3,
  maxOpacity: 0.3,
  minOpacity: 0.08,
} satisfies KeyPoseGhostOptionsState['onionSkin'];

export const KEY_POSE_ONION_SKIN_PREVIOUS_COLOR = '#0fd23a';
export const KEY_POSE_ONION_SKIN_FUTURE_COLOR = '#ff3f56';

export function selectKeyPoseOnionSkinDescriptors({
  currentPlaybackFrame,
  entries,
  options,
}: {
  currentPlaybackFrame: number;
  entries: readonly KeyPoseOnionSkinFrameEntry[];
  options: KeyPoseGhostOptionsState['onionSkin'];
}) {
  if (!options.enabled) {
    return [];
  }

  const ghostsPerDirection = getValidGhostsPerDirection(
    options.ghostsPerDirection,
  );
  if (ghostsPerDirection === 0) {
    return [];
  }

  const sourceEntries = entries
    .map((entry, index) => ({ ...entry, index }))
    .filter((entry) => Number.isFinite(entry.playbackFrame));
  const previous = sourceEntries
    .filter((entry) => entry.playbackFrame <= currentPlaybackFrame)
    .sort(
      (a, b) => b.playbackFrame - a.playbackFrame || a.index - b.index,
    )
    .slice(0, ghostsPerDirection);
  const future = sourceEntries
    .filter((entry) => entry.playbackFrame > currentPlaybackFrame)
    .sort(
      (a, b) => a.playbackFrame - b.playbackFrame || a.index - b.index,
    )
    .slice(0, ghostsPerDirection);

  return [
    ...previous.map((entry, rank) =>
      createKeyPoseOnionSkinDescriptor(entry, rank, 'previous', options),
    ),
    ...future.map((entry, rank) =>
      createKeyPoseOnionSkinDescriptor(entry, rank, 'future', options),
    ),
  ];
}

export function getKeyPoseOnionSkinColor(
  direction: KeyPoseOnionSkinDirection,
) {
  return direction === 'previous'
    ? KEY_POSE_ONION_SKIN_PREVIOUS_COLOR
    : KEY_POSE_ONION_SKIN_FUTURE_COLOR;
}

function createKeyPoseOnionSkinDescriptor(
  entry: KeyPoseOnionSkinFrameEntry,
  rank: number,
  direction: KeyPoseOnionSkinDirection,
  options: KeyPoseGhostOptionsState['onionSkin'],
) {
  return {
    keyPoseId: entry.keyPoseId,
    playbackFrame: entry.playbackFrame,
    direction,
    opacity: getKeyPoseOnionSkinOpacity(rank, options),
    rank,
  } satisfies KeyPoseOnionSkinDescriptor;
}

function getKeyPoseOnionSkinOpacity(
  rank: number,
  options: KeyPoseGhostOptionsState['onionSkin'],
) {
  const maxRank = Math.max(
    1,
    getValidGhostsPerDirection(options.ghostsPerDirection) - 1,
  );
  const t = clamp(rank / maxRank, 0, 1);

  return options.maxOpacity + t * (options.minOpacity - options.maxOpacity);
}

function getValidGhostsPerDirection(ghostsPerDirection: number) {
  if (!Number.isFinite(ghostsPerDirection)) {
    return DEFAULT_KEY_POSE_ONION_SKIN_OPTIONS.ghostsPerDirection;
  }

  return Math.max(0, Math.round(ghostsPerDirection));
}

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}
